import React, { useEffect, useMemo, useRef, useState } from "react";
import { IoArrowBackOutline, IoPerson } from "react-icons/io5";
import { IoIosSend } from "react-icons/io";
import AppCopy from "copy/AppCopy";
import SentMessageCard from "components/card/SentMessageCard";
import RecievedMessageCard from "components/card/RecievedMessageCard";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/store";
import {
  removeMessage,
  resetActiveThread,
  setMessageCount,
  setMessages,
  setMessageStatus,
  upsertMessage,
} from "reducers/chatReducer";
import { useNavigate } from "react-router-dom";
import { Paths } from "router/Paths";
import useChat from "hook/useChat";
import { MessageType } from "types/loadedData";
import { getMonthDate } from "functions/text";
import { SocketEvents, getConversationRoom } from "config/socket";
import {
  subscribeToSocketEvent,
  syncSocketRoom,
} from "utils/socketClient";

type ScrollSnapshot = {
  scrollTop: number;
  scrollHeight: number;
};

const resolveEntityId = (value?: any) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return String(value._id || "");
};

const isSameDay = (date1?: string, date2?: string): boolean => {
  if (!date1 || !date2) return false;

  const first = new Date(date1);
  const second = new Date(date2);

  return (
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()
  );
};

function MessagesContainer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    getConversation,
    deleteMessage,
    getMessages,
    markConversationRead,
    sendMessage,
  } = useChat();
  const { chat, messages, messageCount } = useSelector(
    (state: RootState) => state.chat
  );
  const { user } = useSelector((state: RootState) => state.user);
  const messagesRef = useRef<HTMLDivElement>(null);
  const scrollSnapshotRef = useRef<ScrollSnapshot | null>(null);
  const initialScrollDoneRef = useRef<boolean>(false);
  const isAtBottomRef = useRef<boolean>(true);
  const previousLatestMessageKeyRef = useRef<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [loadingMessages, setLoadingMessages] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [messageText, setMessage] = useState<string>("");
  const [loadError, setLoadError] = useState<boolean>(false);
  const loadDataRef = useRef<() => Promise<void>>(async () => undefined);
  const getConversationRef = useRef(getConversation);
  const markConversationReadRef = useRef(markConversationRead);
  getConversationRef.current = getConversation;
  markConversationReadRef.current = markConversationRead;

  const customer = useMemo(() => {
    const participant = chat?.customer;
    if (participant && typeof participant === "object") {
      return participant;
    }

    return null;
  }, [chat?.customer]);

  const customerId = useMemo(() => resolveEntityId(chat?.customer), [chat?.customer]);
  const currentUserId = resolveEntityId(user?._id);
  const displayMessages = useMemo(() => [...messages].reverse(), [messages]);
  const latestMessageKey = useMemo(() => {
    const latestMessage = displayMessages[displayMessages.length - 1];

    return (
      latestMessage?._id ||
      latestMessage?.clientMessageId ||
      latestMessage?.timestamp ||
      ""
    );
  }, [displayMessages]);
  const titleText = customer?.name || "Conversation";
  const subtitleText =
    customer?.phoneNumber ||
    (customerId ? `Conversation #${customerId.slice(-6)}` : "Conversation details");
  const updatedText = getMonthDate(
    chat?.updatedAt || chat?.createdAt || new Date().toISOString()
  );
  const messageTotalText = messageCount || displayMessages.length;

  const resetScrollState = () => {
    scrollSnapshotRef.current = null;
    initialScrollDoneRef.current = false;
    isAtBottomRef.current = true;
    previousLatestMessageKeyRef.current = undefined;
  };

  const scrollToBottom = () => {
    if (!messagesRef.current) return;
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  };

  const loadData = async () => {
    if (!chat?._id) return;
    setLoadError(false);

    const loadedMessages = await getMessages(
      { id: chat._id, setLoading: setLoadingMessages },
      { reload: true }
    );
    if (loadedMessages === null) {
      setLoadError(true);
      return;
    }

    await markConversationRead({ id: chat._id });
  };
  loadDataRef.current = loadData;

  useEffect(() => {
    if (!chat?._id) {
      dispatch(setMessages([]));
      dispatch(setMessageCount(0));
      resetScrollState();
      return;
    }

    dispatch(setMessages([]));
    dispatch(setMessageCount(0));
    resetScrollState();
    void loadDataRef.current();
  }, [chat?._id, dispatch]);

  useEffect(() => {
    if (!chat?._id) return;
    if (customer?.name) return;

    let cancelled = false;

    void getConversationRef.current({ id: chat._id }, { reload: true }).then(() => {
      if (cancelled) return;
    });

    return () => {
      cancelled = true;
    };
  }, [chat?._id, customer?.name]);

  useEffect(() => {
    if (!chat?._id) return;

    const unsubscribeRoom = syncSocketRoom(getConversationRoom(chat._id));
    const unsubscribeConversationMessage = subscribeToSocketEvent<{
      conversationId: string;
      message: MessageType;
    }>(SocketEvents.ConversationMessage, async (payload) => {
      if (payload.conversationId !== chat._id) return;

      dispatch(
        upsertMessage({
          ...payload.message,
          status: "sent",
        })
      );

      if (
        payload.message.sender !== user?._id ||
        isAtBottomRef.current
      ) {
        requestAnimationFrame(() => {
          scrollToBottom();
        });
      }

      if (payload.message.sender !== user?._id) {
        await markConversationReadRef.current({ id: chat._id });
      }
    });
    const unsubscribeConversationMessageDeleted = subscribeToSocketEvent<{
      conversationId: string;
      messageId: string;
    }>(SocketEvents.ConversationMessageDeleted, (payload) => {
      if (payload.conversationId !== chat._id) return;

      dispatch(removeMessage({ messageId: payload.messageId }));
    });

    return () => {
      unsubscribeConversationMessage();
      unsubscribeConversationMessageDeleted();
      unsubscribeRoom();
    };
  }, [chat?._id, user?._id, dispatch]);

  useEffect(() => {
    if (!chat?._id || !messagesRef.current) return;

    if (scrollSnapshotRef.current) {
      const snapshot = scrollSnapshotRef.current;

      requestAnimationFrame(() => {
        const node = messagesRef.current;
        if (!node) return;

        const nextScrollHeight = node.scrollHeight;
        node.scrollTop =
          snapshot.scrollTop + (nextScrollHeight - snapshot.scrollHeight);
        scrollSnapshotRef.current = null;
      });

      previousLatestMessageKeyRef.current = latestMessageKey;
      initialScrollDoneRef.current = true;
      return;
    }

    if (!displayMessages.length) return;

    if (!initialScrollDoneRef.current) {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
      initialScrollDoneRef.current = true;
      previousLatestMessageKeyRef.current = latestMessageKey;
      return;
    }

    if (previousLatestMessageKeyRef.current !== latestMessageKey) {
      if (isAtBottomRef.current) {
        requestAnimationFrame(() => {
          scrollToBottom();
        });
      }

      previousLatestMessageKeyRef.current = latestMessageKey;
    }
  }, [chat?._id, displayMessages.length, latestMessageKey]);

  const handleBack = () => {
    dispatch(resetActiveThread());
  };

  const handleProfilePress = () => {
    if (!customerId) return;
    navigate(`${Paths.users}/${customerId}`);
  };

  const handleDeleteMessageRequest = (
    targetMessage: MessageType,
    position?: { x: number; y: number }
  ) => {
    if (targetMessage.sender !== user?._id) return;
    if (targetMessage.status === "sending") return;

    setDeleteMenuMessage(targetMessage);
    setDeleteMenuPosition(position || null);
  };

  const [deleteMenuMessage, setDeleteMenuMessage] =
    useState<MessageType | null>(null);
  const [deleteMenuPosition, setDeleteMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [deletingMessage, setDeletingMessage] = useState<boolean>(false);

  const closeDeleteMenu = () => {
    setDeleteMenuMessage(null);
    setDeleteMenuPosition(null);
  };

  const handleDeleteMessageConfirm = async () => {
    if (!deleteMenuMessage) return;

    const targetMessage = deleteMenuMessage;
    closeDeleteMenu();

    const isLocalOnlyMessage =
      !targetMessage._id || String(targetMessage._id).startsWith("pending-");

    if (isLocalOnlyMessage) {
      dispatch(
        removeMessage({
          messageId: targetMessage._id,
          clientMessageId: targetMessage.clientMessageId,
        })
      );
      return;
    }

    await deleteMessage({ id: String(targetMessage._id), setLoading: setDeletingMessage });
  };

  const handleSend = async (text: string) => {
    const normalizedText = text.trim();
    if (!normalizedText || sending) return;
    if (!chat?._id) return;

    const timestamp = new Date().toISOString();
    const clientMessageId = `msg-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    dispatch(
      upsertMessage({
        _id: `pending-${clientMessageId}`,
        clientMessageId,
        sender: user?._id || "",
        conversation: chat._id,
        text: normalizedText,
        timestamp,
        createdAt: timestamp,
        updatedAt: timestamp,
        status: "sending",
      })
    );

    setMessage("");
    requestAnimationFrame(() => scrollToBottom());

    const success = await sendMessage({
      setLoading: setSending,
      text: normalizedText,
      id: chat._id,
      clientMessageId,
    });

    if (success) {
      requestAnimationFrame(() => scrollToBottom());
    } else {
      dispatch(
        setMessageStatus({
          clientMessageId,
          status: "failed",
        })
      );
    }
  };

  const handleRetryMessage = async (targetMessage: MessageType) => {
    if (sending || !chat?._id || !targetMessage.clientMessageId) return;

    dispatch(
      setMessageStatus({
        clientMessageId: targetMessage.clientMessageId,
        status: "sending",
      })
    );

    const success = await sendMessage({
      setLoading: setSending,
      text: targetMessage.text,
      id: chat._id,
      clientMessageId: targetMessage.clientMessageId,
    });

    if (!success) {
      dispatch(
        setMessageStatus({
          clientMessageId: targetMessage.clientMessageId,
          status: "failed",
        })
      );
    }
  };

  const handleLoadMoreMessages = async () => {
    if (!chat?._id) return;
    if (loadingMore) return;
    if (messageCount <= messages.length) return;

    if (messagesRef.current) {
      scrollSnapshotRef.current = {
        scrollTop: messagesRef.current.scrollTop,
        scrollHeight: messagesRef.current.scrollHeight,
      };
    }

    await getMessages(
      {
        id: chat._id,
        offset: messages.length,
        setLoading: setLoadingMore,
      },
      { more: true }
    );
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const distanceFromBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    isAtBottomRef.current = distanceFromBottom <= 48;

    if (target.scrollTop <= 24) {
      void handleLoadMoreMessages();
    }
  };

  const isReceivedMessage = (message: MessageType) => {
    if (currentUserId) {
      return String(message.sender) !== currentUserId;
    }

    return false;
  };

  const handleListingPress = (message: MessageType) => {
    const linkedId = resolveEntityId(message.carId || chat?.car);
    if (linkedId) navigate(`${Paths.cars}/${linkedId}`);
  };

  const { messagePlaceholder } = AppCopy.messagesPage;

  const renderMessage = (message: MessageType, index: number) => {
    const showDateHeader =
      index === 0 ||
      !isSameDay(
        message.timestamp || message.createdAt,
        displayMessages[index - 1]?.timestamp ||
          displayMessages[index - 1]?.createdAt
      );

    return (
      <div key={message._id || message.clientMessageId || index}>
        {showDateHeader && (
          <div className={ClassName.dateHeader}>
            <span>{getMonthDate(message.timestamp || message.createdAt)}</span>
          </div>
        )}

        {isReceivedMessage(message) ? (
          <RecievedMessageCard message={message} onListingPress={handleListingPress} />
        ) : (
          <SentMessageCard
            message={message}
            onDeleteRequest={handleDeleteMessageRequest}
            onRetry={handleRetryMessage}
            onListingPress={handleListingPress}
          />
        )}
      </div>
    );
  };

  if (!chat) {
    return (
      <div className={ClassName.container}>
        <div className={ClassName.placeholderWrap}>
          <div className={ClassName.placeholderIcon}>
            <IoPerson size={28} />
          </div>
          <span className={ClassName.placeholderTitle}>Select a conversation</span>
          <span className={ClassName.placeholderText}>
            Pick a thread from the left to read details and reply.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={ClassName.container}>
      <div className={ClassName.header}>
        <button
          type="button"
          className={ClassName.back}
          onClick={handleBack}
          aria-label="Back"
        >
          <IoArrowBackOutline size={26} />
        </button>

        <button
          type="button"
          className={ClassName.headerIdentity}
          onClick={handleProfilePress}
          disabled={!customerId}
        >
          <div className={ClassName.imageContainer}>
            <IoPerson size={20} />
          </div>
          <div className={ClassName.headerText}>
            <span className={ClassName.name}>{titleText}</span>
            <span className={ClassName.subtitle}>{subtitleText}</span>
          </div>
        </button>

        <div className={ClassName.threadMeta}>
          <span>{messageTotalText} messages</span>
          <span aria-hidden="true">&middot;</span>
          <span>{updatedText}</span>
        </div>
      </div>

      <div
        className={`${ClassName.messagesContainer} ${
          loadingMessages && displayMessages.length === 0
            ? ClassName.messagesLoading
            : ""
        }`}
        ref={messagesRef}
        onScroll={handleScroll}
      >
        {loadingMessages && displayMessages.length === 0 ? (
          <div className={ClassName.loaderContainer}>
            <div className="loader"></div>
          </div>
        ) : loadError && displayMessages.length === 0 ? (
          <div className={ClassName.emptyMessagesState}>
            <span className={ClassName.emptyMessagesTitle}>
              Messages could not be loaded
            </span>
            <span className={ClassName.emptyMessagesText}>
              Check the connection and try again.
            </span>
            <button type="button" className={ClassName.retryButton} onClick={loadData}>
              Try again
            </button>
          </div>
        ) : displayMessages.length === 0 ? (
          <div className={ClassName.emptyMessagesState}>
            <span className={ClassName.emptyMessagesTitle}>
              No messages yet
            </span>
            <span className={ClassName.emptyMessagesText}>
              This thread is ready for the first reply.
            </span>
          </div>
        ) : (
          <>
            {loadingMore && (
              <div className={ClassName.loaderContainer}>
                <div className="loader"></div>
              </div>
            )}
            {displayMessages.map(renderMessage)}
          </>
        )}
      </div>

      <div className={ClassName.footer}>
        <div className={ClassName.messageBar}>
          <textarea
            placeholder={messagePlaceholder}
            className={ClassName.messageInput}
            value={messageText}
            onChange={(event) => setMessage(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void handleSend(messageText);
              }
            }}
            rows={1}
          />
          <button
            type="button"
            aria-label="Send message"
            className={ClassName.sendButton}
            onClick={() => void handleSend(messageText)}
            disabled={sending || !messageText.trim()}
          >
            {sending ? <div className="loader"></div> : <IoIosSend size={20} />}
          </button>
        </div>
      </div>

      <div className={ClassName.deleteMenuMount}>
        {deleteMenuMessage && (
          <div className={ClassName.deleteMenuOverlay} onClick={closeDeleteMenu}>
            <div
              className={ClassName.deleteMenuCard}
              style={getDeleteMenuCardStyle()}
              onClick={(event) => event.stopPropagation()}
            >
              <span className={ClassName.deleteMenuTitle}>Delete Message</span>
              <button
                type="button"
                className={ClassName.deleteMenuButton}
                onClick={handleDeleteMessageConfirm}
                disabled={deletingMessage}
              >
                {deletingMessage ? (
                  <div className="loader"></div>
                ) : (
                  <span>Delete</span>
                )}
              </button>
              <button
                type="button"
                className={ClassName.cancelMenuButton}
                onClick={closeDeleteMenu}
                disabled={deletingMessage}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  function getDeleteMenuCardStyle(): React.CSSProperties {
    if (!deleteMenuPosition) {
      return {
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
      };
    }

    const width = 220;
    const height = 120;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const x = Math.min(
      Math.max(12, deleteMenuPosition.x - width / 2),
      viewportWidth - width - 12
    );
    const y = Math.min(
      Math.max(12, deleteMenuPosition.y - height / 2),
      viewportHeight - height - 12
    );

    return { left: x, top: y };
  }
}

export default MessagesContainer;

const ClassName = {
  container:
    "flex flex-col flex-1 border border-bgSecondary rounded-2xl overflow-hidden bg-white shadow-primaryShadow",
  back: "flex md:hidden items-center justify-center w-10 h-10 rounded-full text-secondaryText hover:bg-slate-50 transition-colors",
  header:
    "border-b border-bgSecondary min-h-16 px-4 py-3 w-full flex items-center gap-4 bg-white",
  headerIdentity:
    "flex items-center gap-3 text-left min-w-0 flex-1 disabled:opacity-70 disabled:cursor-default",
  imageContainer:
    "flex flex-shrink-0 items-center justify-center w-10 h-10 rounded-full bg-bgSecondary text-secondaryText border border-bgSecondary",
  headerText: "flex flex-col min-w-0 gap-0.5",
  name: "text-primaryText text-base font-semibold truncate",
  subtitle: "text-secondaryText text-xs truncate",
  threadMeta:
    "hidden lg:flex items-center gap-2 flex-shrink-0 ml-auto text-xs text-secondaryText",
  footer:
    "flex items-center justify-center p-3 px-4 bg-white border-t border-bgSecondary",
  messageBar:
    "min-h-12 w-full bg-slate-50 rounded-2xl flex items-end p-2.5 px-4 border border-gray-200 gap-3",
  messagesContainer:
    "flex flex-col flex-1 gap-3 px-4 py-4 overflow-y-auto scroll-hide bg-slate-50 justify-end",
  messagesLoading: "justify-start",
  sendButton:
    "w-11 h-11 flex items-center justify-center rounded-full bg-primaryText text-white cursor-pointer transition-opacity disabled:cursor-not-allowed disabled:opacity-35",
  messageInput:
    "flex bg-transparent flex-1 resize-none outline-none py-2 min-h-8 max-h-28",
  dateHeader:
    "w-full flex items-center justify-center text-xs text-secondaryText py-2",
  loaderContainer: "w-full flex items-center justify-center py-6",
  placeholderWrap:
    "flex flex-1 items-center justify-center flex-col gap-3 px-8 text-center text-secondaryText",
  placeholderIcon:
    "w-14 h-14 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center border border-slate-200",
  placeholderTitle: "text-primaryText text-lg font-semibold",
  placeholderText: "text-sm max-w-sm leading-6",
  emptyContainer: "flex items-center justify-center",
  emptyMessagesState:
    "flex w-full flex-col items-center justify-center min-h-[280px] gap-2 text-center",
  emptyMessagesTitle: "text-primaryText text-base font-semibold",
  emptyMessagesText: "text-sm text-secondaryText max-w-sm leading-6",
  retryButton:
    "mt-2 rounded-xl bg-primaryText px-4 py-2 text-xs font-semibold text-white",
  deleteMenuMount: "",
  deleteMenuOverlay:
    "fixed inset-0 z-50 bg-black/30",
  deleteMenuCard:
    "absolute w-[220px] rounded-2xl bg-white border border-slate-200 shadow-xl p-4 flex flex-col gap-3",
  deleteMenuTitle: "text-primaryText font-semibold",
  deleteMenuButton:
    "w-full rounded-xl bg-primary/10 text-primary font-semibold py-2.5 disabled:opacity-60",
  cancelMenuButton:
    "w-full rounded-xl border border-slate-200 text-secondaryText font-medium py-2.5 disabled:opacity-60",
};
